<script setup>
import { reactive, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();

const initialState = {
  name: '',
  description: '',
  productName: '',
  autoResolveDisabled: false,
  autoResolveDuration: 1,
  autoResolveUnit: 'hours',
  features: {
    conversationFaqs: false,
    memories: false,
    citations: false,
    contactAttributes: false,
    pastConversations: false,
  },
};

const state = reactive({ ...initialState });

const validationRules = {
  name: { required, minLength: minLength(1) },
  description: { required, minLength: minLength(1) },
  productName: { required, minLength: minLength(1) },
};

const v$ = useVuelidate(validationRules, state);

const getErrorMessage = field => {
  return v$.value[field].$error ? v$.value[field].$errors[0].$message : '';
};

const formErrors = computed(() => ({
  name: getErrorMessage('name'),
  description: getErrorMessage('description'),
  productName: getErrorMessage('productName'),
}));

const updateStateFromAssistant = assistant => {
  const { config = {} } = assistant;
  state.name = assistant.name;
  state.description = assistant.description;
  state.productName = config.product_name;
  state.autoResolveDisabled = config.auto_resolve_disabled || false;
  state.autoResolveDuration = config.auto_resolve_duration || 1;
  state.autoResolveUnit = config.auto_resolve_unit || 'hours';
  state.features = {
    conversationFaqs: config.feature_faq || false,
    memories: config.feature_memory || false,
    citations: config.feature_citation || false,
    contactAttributes: config.feature_contact_attributes || false,
    pastConversations: config.feature_past_conversations || false,
  };
};

const handleBasicInfoUpdate = async () => {
  const result = await Promise.all([
    v$.value.name.$validate(),
    v$.value.description.$validate(),
    v$.value.productName.$validate(),
  ]).then(results => results.every(Boolean));
  if (!result) return;

  const payload = {
    name: state.name,
    description: state.description,
    config: {
      ...props.assistant.config,
      product_name: state.productName,
      auto_resolve_disabled: state.autoResolveDisabled,
      auto_resolve_duration: Number(state.autoResolveDuration) || 1,
      auto_resolve_unit: state.autoResolveUnit,
      feature_faq: state.features.conversationFaqs,
      feature_memory: state.features.memories,
      feature_citation: state.features.citations,
      feature_contact_attributes: state.features.contactAttributes,
      feature_past_conversations: state.features.pastConversations,
    },
  };

  emit('submit', payload);
};

watch(
  () => props.assistant,
  newAssistant => {
    if (newAssistant) updateStateFromAssistant(newAssistant);
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-6">
    <Input
      v-model="state.name"
      :label="t('CAPTAIN.ASSISTANTS.FORM.NAME.LABEL')"
      :placeholder="t('CAPTAIN.ASSISTANTS.FORM.NAME.PLACEHOLDER')"
      :message="formErrors.name"
      :message-type="formErrors.name ? 'error' : 'info'"
    />

    <Input
      v-model="state.productName"
      :label="t('CAPTAIN.ASSISTANTS.FORM.PRODUCT_NAME.LABEL')"
      :placeholder="t('CAPTAIN.ASSISTANTS.FORM.PRODUCT_NAME.PLACEHOLDER')"
      :message="formErrors.productName"
      :message-type="formErrors.productName ? 'error' : 'info'"
    />

    <Editor
      v-model="state.description"
      :label="t('CAPTAIN.ASSISTANTS.FORM.DESCRIPTION.LABEL')"
      :placeholder="t('CAPTAIN.ASSISTANTS.FORM.DESCRIPTION.PLACEHOLDER')"
      :message="formErrors.description"
      :message-type="formErrors.description ? 'error' : 'info'"
      class="z-0"
    />

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.TITLE') }}
      </label>
      <div class="flex flex-col gap-2">
        <label class="flex items-center gap-2">
          <input v-model="state.features.conversationFaqs" type="checkbox" />
          {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.ALLOW_CONVERSATION_FAQS') }}
        </label>
        <label class="flex items-center gap-2">
          <input v-model="state.features.memories" type="checkbox" />
          {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.ALLOW_MEMORIES') }}
        </label>
        <label class="flex items-center gap-2">
          <input v-model="state.features.citations" type="checkbox" />
          {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.ALLOW_CITATIONS') }}
        </label>
        <label class="flex items-center gap-2">
          <input v-model="state.features.contactAttributes" type="checkbox" />
          {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.ALLOW_CONTACT_ATTRIBUTES') }}
        </label>
        <label class="flex items-center gap-2">
          <input v-model="state.features.pastConversations" type="checkbox" />
          Allow AI to learn from past agent conversations
        </label>
      </div>
    </div>

    <!-- Auto-Resolve Settings -->
    <div class="flex flex-col gap-3 p-4 border border-n-weak rounded-xl bg-n-alpha-1 dark:bg-n-solid-2">
      <div class="flex items-center justify-between">
        <div class="flex flex-col">
          <span class="text-sm font-semibold text-n-slate-12">
            Auto-Resolve Obrolan Otomatis (Captain AI)
          </span>
          <span class="text-xs text-n-slate-11">
            Mengatur apakah Captain AI dapat secara otomatis menyelesaikan obrolan yang tidak direspon.
          </span>
        </div>
      </div>

      <label class="flex items-center gap-2 cursor-pointer mt-1">
        <input v-model="state.autoResolveDisabled" type="checkbox" class="w-4 h-4 rounded text-purple-600 focus:ring-purple-500" />
        <span class="text-sm text-n-slate-12 font-medium">
          Nonaktifkan Auto-Resolve Otomatis (Penutupan obrolan 100% diserahkan ke staf manusia)
        </span>
      </label>

      <div v-if="!state.autoResolveDisabled" class="flex flex-col gap-2 mt-2 pt-2 border-t border-n-weak">
        <label class="text-xs font-medium text-n-slate-11">
          Batas Waktu Obrolan Inaktif Sebelum Diselesaikan AI:
        </label>
        <div class="flex items-center gap-3">
          <input
            v-model="state.autoResolveDuration"
            type="number"
            min="1"
            max="365"
            class="w-24 px-3 py-1.5 text-sm border border-n-weak rounded-lg bg-n-alpha-1 dark:bg-n-solid-3 text-n-slate-12"
          />
          <select
            v-model="state.autoResolveUnit"
            class="px-3 py-1.5 text-sm border border-n-weak rounded-lg bg-n-alpha-1 dark:bg-n-solid-3 text-n-slate-12"
          >
            <option value="minutes">Menit</option>
            <option value="hours">Jam</option>
            <option value="days">Hari</option>
            <option value="weeks">Minggu</option>
            <option value="months">Bulan</option>
          </select>
        </div>
      </div>
    </div>

    <div>
      <Button
        :label="t('CAPTAIN.ASSISTANTS.FORM.UPDATE')"
        @click="handleBasicInfoUpdate"
      />
    </div>
  </div>
</template>
